import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  adminApi,
  type AgentTypeWithRoles,
  type Company,
  type DeviceListItem,
  type Project,
} from '../api/admin';
import { DataListFrame, sortArrow, useListControls } from '../components/data-list';
import { Modal } from '../components/modal';
import { Danger, DangerDeleteDialog } from '../components/danger-delete-dialog';

// プロジェクト＝フラットな仕事（≒デバイスマウント）（ADR0003）。会社は親ではない。開発系/会社系は type で区別するだけ。
// このメニューの責務は「プロジェクトの台帳（作成・変更・削除）」と「どのデバイスマウントで動くか」の定義。
// 人（エージェント）の割り当ては **ここでは行わない** → 別メニュー「アサイン」で project ⇄ agent を結ぶ。
//
// 操作体系は teams 画面に統一：一覧 → ＋新規作成（ダイアログ）→ 各行「変更/削除」。
// 「変更」ダイアログの中でデバイスマウントの割り当てを編集する。type は #176 区分マスタを参照（ベタ打ち廃止）。

// プロジェクトの「所属(会社)」UI は当面非表示。会社管理画面が無く、現状 seed の1社のみで
// プロジェクトの所属(companyId)は実質未使用のため(社長判断 2026-06-09)。Company モデル/API/
// companyId 保存ロジックは温存しているので、会社管理UIを作る際に true へ戻せば即復活する。
const SHOW_PROJECT_COMPANY_FIELD = false;

type ProjectSortField = 'id' | 'name' | 'type';
const SORT_OPTIONS: { value: ProjectSortField; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'name', label: 'プロジェクト名' },
  { value: 'type', label: '区分' },
];

export function ProjectsPage() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['projects'] });
    qc.invalidateQueries({ queryKey: ['devices'] });
    qc.invalidateQueries({ queryKey: ['agents'] });
  };

  const devicesQuery = useQuery({ queryKey: ['devices'], queryFn: adminApi.listDevices });
  const companiesQuery = useQuery({ queryKey: ['companies'], queryFn: adminApi.listCompanies });
  const mastersQuery = useQuery({ queryKey: ['agent-masters'], queryFn: adminApi.listAgentMasters });

  const devices = devicesQuery.data ?? [];
  const companies = companiesQuery.data ?? [];
  // #176 区分マスタ（enabled のみ）。type の選択肢・ラベル・絞り込みドロップダウンはここから出す。
  const masterTypes = (mastersQuery.data?.types ?? []).filter((t) => t.enabled);
  const typeLabelOf = (code: string) =>
    mastersQuery.data?.types.find((t) => t.code === code)?.label ?? code;

  const controls = useListControls<ProjectSortField>('konecco.list.projects', {
    q: '',
    sortBy: 'id',
    sortDir: 'asc',
    page: 1,
    pageSize: 20,
  });
  // 区分(type)の絞り込み（マスタ＝ドロップダウン）。'' は指定なし。変更時はページを 1 に戻す。
  const [typeFilter, setTypeFilter] = useState('');

  // 検索/並び替え/ページングは常にサーバ側（お手本: エージェント一覧）。全件取得はしない。
  const projectsQuery = useQuery({
    queryKey: [
      'projects',
      'search',
      controls.q,
      typeFilter,
      controls.sortBy,
      controls.sortDir,
      controls.page,
      controls.pageSize,
    ],
    queryFn: () =>
      adminApi.searchProjects({
        q: controls.q || undefined,
        type: typeFilter || undefined,
        sortBy: controls.sortBy,
        sortDir: controls.sortDir,
        page: controls.page,
        pageSize: controls.pageSize,
      }),
    placeholderData: keepPreviousData,
  });
  const result = projectsQuery.data;
  const pageItems = result?.items ?? [];
  const total = result?.total ?? 0;

  const [createOpen, setCreateOpen] = useState(false);
  // 編集対象は ID 保持し、現在ページの一覧から引き直す（割り当て変更で invalidate されても追従）。
  // 「基礎情報」と「デバイスマウント割当」は別モーダルに分離（社長指示 2026-06-09）。別 ID 状態で持つ。
  const [infoId, setInfoId] = useState<number | null>(null);
  const [dirsId, setDirsId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const infoProject = pageItems.find((p) => p.id === infoId) ?? null;
  const dirsProject = pageItems.find((p) => p.id === dirsId) ?? null;
  const deleteProject = pageItems.find((p) => p.id === deleteId) ?? null;

  const createMut = useMutation({
    mutationFn: adminApi.createProject,
    onSuccess: (created) => {
      setCreateOpen(false);
      invalidate();
      // 作成直後はデバイスマウントを続けて割り当てられるよう、そのままデバイスマウント割当ダイアログを開く。
      setDirsId(created.id);
    },
  });
  const updateMut = useMutation({
    mutationFn: (vars: {
      id: number;
      input: {
        name?: string;
        nickname?: string | null;
        type?: string;
        companyId?: number | null;
        personaName?: string;
      };
    }) => adminApi.updateProject(vars.id, vars.input),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: adminApi.deleteProject,
    onSuccess: () => {
      setDeleteId(null);
      invalidate();
    },
  });

  // プロジェクトごとの デバイスマウント件数 / 担当チーム数（一覧表示用）。
  // 担当はアサイン（デバイスマウント⇄チーム）で決まるので、配下デバイスマウントに割り当てられた重複なしチーム数を数える。
  const deviceCountOf = (projectId: number) =>
    devices.reduce(
      (n, d) => n + d.deviceProjectMap.filter((m) => m.projectId === projectId).length,
      0,
    );
  const memberCountOf = (projectId: number) => {
    const teamIds = new Set<number>();
    for (const d of devices)
      for (const m of d.deviceProjectMap)
        if (m.projectId === projectId && m.teamId != null) teamIds.add(m.teamId);
    return teamIds.size;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p className="k-muted" style={{ margin: 0, fontSize: 13 }}>
        プロジェクト＝1つの仕事（≒デバイスマウント）。<strong>会社は親ではありません</strong>（すべてフラット）。
        各行の「変更」でデバイスマウントを割り当てます。デバイスマウントへの担当チームの割り当ては別メニュー
        <strong>「アサイン」</strong>で行います。
      </p>
      {projectsQuery.error && (
        <p className="k-error">取得に失敗しました: {(projectsQuery.error as Error).message}</p>
      )}
      {deleteMut.error && (
        <p className="k-error">削除に失敗しました: {(deleteMut.error as Error).message}</p>
      )}

      <DataListFrame
        title="プロジェクト一覧"
        total={total}
        loading={projectsQuery.isLoading}
        controls={controls}
        sortOptions={SORT_OPTIONS}
        searchPlaceholder="あいまい検索…（プロジェクト名・愛称）"
        filters={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="k-muted" style={{ fontSize: 12 }}>
              区分
            </span>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                controls.setPage(1);
              }}
              style={{ padding: '7px 10px', fontSize: 13 }}
            >
              <option value="">すべて</option>
              {masterTypes.map((t) => (
                <option key={t.id} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        }
        rightActions={
          <button type="button" onClick={() => setCreateOpen(true)}>
            ＋ 新規作成
          </button>
        }
      >
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 120px 120px 120px 260px',
              gap: 12,
              padding: '8px 14px',
              background: '#f5f7fa',
              fontSize: 12,
              fontWeight: 600,
              color: '#64748b',
              borderBottom: '1px solid #e7ebf0',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <div
              onClick={() => controls.toggleSort('name')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="クリックで並び替え"
            >
              プロジェクト名
              <span style={{ color: '#2563eb' }}>{sortArrow(controls, 'name')}</span>
            </div>
            <div
              onClick={() => controls.toggleSort('type')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="クリックで並び替え"
            >
              区分
              <span style={{ color: '#2563eb' }}>{sortArrow(controls, 'type')}</span>
            </div>
            <div>デバイスマウント</div>
            <div>担当チーム</div>
            <div style={{ textAlign: 'right' }}>操作</div>
          </div>
          {pageItems.map((p, i) => (
            <ProjectRow
              key={p.id}
              project={p}
              typeLabel={typeLabelOf(p.type)}
              deviceCount={deviceCountOf(p.id)}
              memberCount={memberCountOf(p.id)}
              last={i === pageItems.length - 1}
              onEditInfo={() => setInfoId(p.id)}
              onEditDirs={() => setDirsId(p.id)}
              onDelete={() => setDeleteId(p.id)}
            />
          ))}
        </div>
      </DataListFrame>

      {createOpen && (
        <ProjectCreateDialog
          masterTypes={masterTypes}
          companies={companies}
          submitting={createMut.isPending}
          error={createMut.error as Error | null}
          onClose={() => setCreateOpen(false)}
          onSubmit={(v) => createMut.mutate(v)}
        />
      )}

      {infoProject && (
        <ProjectInfoDialog
          project={infoProject}
          masterTypes={masterTypes}
          companies={companies}
          savingInfo={updateMut.isPending}
          infoError={updateMut.error as Error | null}
          onClose={() => setInfoId(null)}
          onSaveInfo={(input) => updateMut.mutate({ id: infoProject.id, input })}
        />
      )}

      {dirsProject && (
        <ProjectDirsDialog
          project={dirsProject}
          devices={devices}
          onClose={() => setDirsId(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['devices'] })}
        />
      )}

      {deleteProject && (
        <DangerDeleteDialog
          title="プロジェクトを削除"
          targetLabel={deleteProject.nickname || deleteProject.name}
          busy={deleteMut.isPending}
          error={deleteMut.error as Error | null}
          onClose={() => setDeleteId(null)}
          onConfirm={() => deleteMut.mutate(deleteProject.id)}
          message={
            <>
              プロジェクト「<strong>{deleteProject.nickname || deleteProject.name}</strong>」を削除します。
              <Danger>この操作は取り消せません。</Danger>
              <br />
              紐づくものは自動で外れます：
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                <li>担当エージェントは <strong>未割り当て</strong>に戻ります（エージェント自体は消えません）</li>
                <li>デバイスマウントは <strong>未割当</strong>に戻ります（チャットの宛先から外れます）</li>
                <li>このプロジェクトの擬人（会話相手）は<Danger>削除</Danger>されます</li>
              </ul>
            </>
          }
        />
      )}
    </div>
  );
}

function ProjectRow({
  project,
  typeLabel,
  deviceCount,
  memberCount,
  last,
  onEditInfo,
  onEditDirs,
  onDelete,
}: {
  project: Project;
  typeLabel: string;
  deviceCount: number;
  memberCount: number;
  last: boolean;
  onEditInfo: () => void;
  onEditDirs: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 120px 120px 120px 260px',
        gap: 12,
        padding: '12px 14px',
        alignItems: 'center',
        background: '#fff',
        borderBottom: last ? 'none' : '1px solid #eef2f7',
      }}
    >
      <div style={{ minWidth: 0 }}>
        {/* 愛称があれば主表示。dev コード名(name)は補助で併記。 */}
        <strong style={{ fontSize: 15 }}>{project.nickname || project.name}</strong>
        {project.nickname && (
          <span
            className="k-muted"
            style={{ fontSize: 12, marginLeft: 8, fontFamily: 'monospace' }}
          >
            #{project.name}
          </span>
        )}
        {project.personaName && (
          <span className="k-muted" style={{ fontSize: 12, marginLeft: 8 }}>
            🧑 {project.personaName}
          </span>
        )}
      </div>
      <div>
        <span className="k-badge">{typeLabel}</span>
      </div>
      <div style={{ fontSize: 13 }}>
        {deviceCount === 0 ? (
          <span className="k-muted">未定義</span>
        ) : (
          <span style={{ color: '#15803d' }}>🖥 {deviceCount}</span>
        )}
      </div>
      <div style={{ fontSize: 13 }}>
        {memberCount === 0 ? (
          <span className="k-muted">未アサイン</span>
        ) : (
          <span>👥 {memberCount}</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button className="btn-ghost" type="button" onClick={onEditInfo}>
          基礎情報
        </button>
        <button className="btn-ghost" type="button" onClick={onEditDirs}>
          デバイスマウント割当
        </button>
        <button className="btn-danger" type="button" onClick={onDelete}>
          削除
        </button>
      </div>
    </div>
  );
}

const labelStyle = { fontSize: 13, fontWeight: 600, color: '#334155' } as const;
const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 4 } as const;

// 新規作成ダイアログ：基本情報のみ。デバイスマウントは作成後に開く変更ダイアログで定義する。
function ProjectCreateDialog({
  masterTypes,
  companies,
  onSubmit,
  onClose,
  submitting,
  error,
}: {
  masterTypes: AgentTypeWithRoles[];
  companies: Company[];
  onSubmit: (v: {
    name: string;
    nickname?: string;
    type: string;
    companyId?: number;
    personaName?: string;
  }) => void;
  onClose: () => void;
  submitting: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [type, setType] = useState(masterTypes[0]?.code ?? 'dev');
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [personaName, setPersonaName] = useState('');

  return (
    <Modal title="新しいプロジェクトを作成" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          onSubmit({
            name: name.trim(),
            nickname: nickname.trim() || undefined,
            type,
            companyId: companyId === '' ? undefined : Number(companyId),
            personaName: personaName.trim() || undefined,
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={fieldWrap}>
          <label style={labelStyle}>プロジェクト名（dev コード名・例: sales-stocars）</label>
          <input
            placeholder="例: sales-stocars"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            style={{ fontFamily: 'monospace' }}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>愛称（日本語の表示名・任意）</label>
          <input
            placeholder="例: セールスストカーズ"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
          />
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            設定すると一覧などで愛称が優先表示されます（コード名は併記）。
          </p>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>区分（マスタ管理）</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {masterTypes.length === 0 && <option value="dev">開発系</option>}
            {masterTypes.map((t) => (
              <option key={t.id} value={t.code}>
                {t.label}
              </option>
            ))}
          </select>
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            選択肢は「マスタ管理」の区分に追従します。
          </p>
        </div>
        {SHOW_PROJECT_COMPANY_FIELD && companies.length > 0 && (
          <div style={fieldWrap}>
            <label style={labelStyle}>所属（任意・親階層ではありません）</label>
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">（なし）</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={fieldWrap}>
          <label style={labelStyle}>擬人（会話相手）の表示名（任意・既定: 担当AI）</label>
          <input
            placeholder="例: 田中君"
            value={personaName}
            onChange={(e) => setPersonaName(e.target.value)}
          />
        </div>
        {error && <p className="k-error">{error.message}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" disabled={submitting || !name.trim()}>
            作成
          </button>
        </div>
      </form>
    </Modal>
  );
}

// 基礎情報ダイアログ：名前・愛称・区分・擬人だけを編集する。
// デバイスマウントの割り当ては別ダイアログ（ProjectDirsDialog）に分離（社長指示 2026-06-09）。
function ProjectInfoDialog({
  project,
  masterTypes,
  companies,
  savingInfo,
  infoError,
  onClose,
  onSaveInfo,
}: {
  project: Project;
  masterTypes: AgentTypeWithRoles[];
  companies: Company[];
  savingInfo: boolean;
  infoError: Error | null;
  onClose: () => void;
  onSaveInfo: (input: {
    name?: string;
    nickname?: string | null;
    type?: string;
    companyId?: number | null;
    personaName?: string;
  }) => void;
}) {
  const [name, setName] = useState(project.name);
  const [nickname, setNickname] = useState(project.nickname ?? '');
  const [type, setType] = useState(project.type);
  const [companyId, setCompanyId] = useState<number | ''>(project.companyId ?? '');
  const [personaName, setPersonaName] = useState(project.personaName ?? '');

  // 保存成功フィードバック: 保存中(savingInfo) が true→false へ遷移し、かつエラーが無ければ
  // 「✓ 保存しました」を一瞬出す(トーストlib未導入のため軽量実装)。ダイアログは閉じない
  // (続けて別項目を編集できるよう、明示の「閉じる」で閉じる)。
  const [justSaved, setJustSaved] = useState(false);
  const wasSavingRef = useRef(false);
  useEffect(() => {
    const wasSaving = wasSavingRef.current;
    wasSavingRef.current = savingInfo;
    if (wasSaving && !savingInfo && !infoError) {
      setJustSaved(true);
      const t = setTimeout(() => setJustSaved(false), 2200);
      return () => clearTimeout(t);
    }
  }, [savingInfo, infoError]);

  const infoDirty =
    name.trim() !== project.name ||
    (nickname.trim() || '') !== (project.nickname ?? '') ||
    type !== project.type ||
    (companyId === '' ? null : Number(companyId)) !== (project.companyId ?? null) ||
    (personaName.trim() || '') !== (project.personaName ?? '');

  return (
    <Modal
      title={`基礎情報を編集 — ${project.nickname || project.name}`}
      onClose={onClose}
      width={520}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* 基礎情報 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={fieldWrap}>
            <label style={labelStyle}>プロジェクト名（dev コード名）</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={{ fontFamily: 'monospace' }}
            />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>愛称(日本語の表示名・任意)</label>
            <input
              placeholder="例: セールスストカーズ"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ ...fieldWrap, flex: 1, minWidth: 180 }}>
              <label style={labelStyle}>区分（マスタ管理）</label>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                {/* 現在値がマスタ無効/未収載でも消えないよう、無ければ現状値を出す */}
                {!masterTypes.some((t) => t.code === type) && (
                  <option value={type}>{type}</option>
                )}
                {masterTypes.map((t) => (
                  <option key={t.id} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            {SHOW_PROJECT_COMPANY_FIELD && companies.length > 0 && (
              <div style={{ ...fieldWrap, flex: 1, minWidth: 180 }}>
                <label style={labelStyle}>所属（任意）</label>
                <select
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">（なし）</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>擬人（会話相手）の表示名</label>
            <input value={personaName} onChange={(e) => setPersonaName(e.target.value)} />
          </div>
          {infoError && <p className="k-error">{infoError.message}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
            {justSaved && (
              <span
                style={{ color: '#16a34a', fontSize: 13, fontWeight: 600 }}
                role="status"
              >
                ✓ 保存しました
              </span>
            )}
            <button
              type="button"
              disabled={!infoDirty || savingInfo || !name.trim()}
              onClick={() =>
                onSaveInfo({
                  name: name.trim(),
                  // 愛称(nickname)は dirty 判定(infoDirty)に含めているのに送信ペイロードから
                  // 漏れていた。そのため愛称を編集して保存しても「エラーも出ず保存もされない」
                  // (再取得で元値に戻り「保存済み」に戻る)状態だった。明示送信する。
                  nickname: nickname.trim() || null,
                  type,
                  companyId: companyId === '' ? null : Number(companyId),
                  personaName: personaName.trim(),
                })
              }
            >
              {savingInfo ? '保存中…' : infoDirty ? '基礎情報を保存' : '保存済み'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            閉じる
          </button>
        </div>
      </div>
    </Modal>
  );
}

// デバイスマウント割当ダイアログ：このプロジェクトにデバイスマウントを割り当てる/外す。
// デバイスマウントはデバイスマウント画面で名前を付けて登録済みの前提。ここでは新規登録しない（社長指示 2026-06-09）。
// 追加＝未割当(projectId=null)のデバイスマウントを選んで割り当てる。外す＝割当解除(projectId=null)でデバイスには残す。
function ProjectDirsDialog({
  project,
  devices,
  onClose,
  onChanged,
}: {
  project: Project;
  devices: DeviceListItem[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [pickMapId, setPickMapId] = useState<number | ''>('');

  const bundle = useMutation({
    mutationFn: (mapId: number) => adminApi.assignPathToProject(mapId, project.id),
    onSuccess: () => {
      setPickMapId('');
      onChanged();
    },
  });
  const unbundle = useMutation({
    mutationFn: (mapId: number) => adminApi.assignPathToProject(mapId, null),
    onSuccess: onChanged,
  });

  // このプロジェクトに割り当てられている (デバイス × マウントポイント) の組。
  const assigned = devices.flatMap((w) =>
    w.deviceProjectMap
      .filter((m) => m.projectId === project.id)
      .map((m) => ({
        mapId: m.id,
        wname: w.nickname || w.name,
        online: w.online,
        label: m.mountPoint.name,
        dir: m.mountPoint.path,
      })),
  );

  // 未割当（projectId=null）のデバイスマウント。デバイス別に出す。
  const availableByDevice = devices
    .map((w) => ({
      wname: w.nickname || w.name,
      paths: w.deviceProjectMap.filter((m) => m.projectId == null),
    }))
    .filter((g) => g.paths.length > 0);
  const hasAvailable = availableByDevice.length > 0;

  return (
    <Modal
      title={`デバイスマウント割当 — ${project.nickname || project.name}`}
      onClose={onClose}
      width={580}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>
          このプロジェクトが「どのデバイスマウントで動くか」を、
          <strong>デバイスマウント画面で登録済みのデバイスマウント</strong> から選んで割り当てます（デバイスごとに、付けたマウントポイント名で選べます）。
          ここで割り当てた組がチャット（会話）の宛先候補になります。デバイスマウントの登録・変更は<strong>デバイスマウント画面</strong>で行います。
        </p>

        {/* 割当済み一覧 */}
        {assigned.length === 0 ? (
          <p className="k-muted" style={{ margin: 0, fontSize: 13 }}>
            まだデバイスマウントを割り当てていません。下で選んで割り当てるとチャットに出ます。
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {assigned.map((a) => (
              <div
                key={a.mapId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  background: '#fff',
                  border: '1px solid #eef2f7',
                  borderRadius: 8,
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: a.online ? '#16a34a' : '#cbd5e1',
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div>
                    <strong>{a.label || '(名前なし)'}</strong>
                    <span className="k-muted" style={{ fontSize: 12, marginLeft: 8 }}>
                      @{a.wname}
                    </span>
                  </div>
                  <span
                    style={{ fontFamily: 'monospace', color: '#94a3b8', fontSize: 12 }}
                  >
                    {a.dir}
                  </span>
                </div>
                <button
                  className="btn-danger"
                  type="button"
                  aria-label="このデバイスマウントを外す"
                  title="割当を外す（デバイスマウントはデバイスに残ります）"
                  style={{ padding: '4px 9px' }}
                  disabled={unbundle.isPending}
                  onClick={() => {
                    if (
                      confirm(
                        `「${a.label || a.dir}」(@${a.wname}) をこのプロジェクトから外しますか？\n（デバイスマウントはデバイスに残り、別プロジェクトに割り当て直せます）`,
                      )
                    )
                      unbundle.mutate(a.mapId);
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 割当追加：未割当のデバイスマウントを選ぶ */}
        <form
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!pickMapId) return;
            bundle.mutate(Number(pickMapId));
          }}
        >
          <select
            value={pickMapId}
            onChange={(e) => setPickMapId(e.target.value ? Number(e.target.value) : '')}
            disabled={!hasAvailable}
            style={{ flex: '1 1 320px' }}
          >
            <option value="">
              {hasAvailable ? '割り当てるデバイスマウントを選択…' : '未割当のデバイスマウントがありません'}
            </option>
            {availableByDevice.map((g) => (
              <optgroup key={g.wname} label={g.wname}>
                {g.paths.map((m) => (
                  // 物理パスそのものではなく、マウントポイントの名前で選ばせる（デバイスは optgroup で識別）。
                  // 同名の取り違え防止に、用途(purpose)があれば併記する。
                  <option key={m.id} value={m.id}>
                    {m.mountPoint.name + (m.purpose ? ` · ${m.purpose}` : '')}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <button type="submit" disabled={!pickMapId || bundle.isPending}>
            割り当てる
          </button>
        </form>
        {!hasAvailable && (
          <p className="k-muted" style={{ margin: 0, fontSize: 12 }}>
            割り当てられるデバイスマウントがありません。デバイスマウント画面でデバイスマウントを登録（名前付け）してから割り当ててください。
          </p>
        )}
        {bundle.isError && (
          <p className="k-error" style={{ margin: 0, fontSize: 13 }}>
            割り当てに失敗しました: {(bundle.error as Error).message}
          </p>
        )}
        {unbundle.isError && (
          <p className="k-error" style={{ margin: 0, fontSize: 13 }}>
            外すのに失敗しました: {(unbundle.error as Error).message}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            閉じる
          </button>
        </div>
      </div>
    </Modal>
  );
}
